import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const ROLE_LABELS = {
  medico_medicina_generale: "Medico di Medicina Generale",
  medico_specialista: "Medico Specialista",
  laboratorio: "Laboratorio",
  medico_base: "Medico di base",
  diagnostica: "Struttura diagnostica",
  altro_professionista: "Altro professionista",
  admin: "Amministratore",
};

// Mappa specializzazione (aggettivo/settore) → sostantivo che descrive lo specialista.
// Serve per mostrare in rubrica direttamente "Cardiologo" al posto di "Medico Specialista".
const SPECIALTY_NOUNS = {
  cardiologia: "Cardiologo",
  dermatologia: "Dermatologo",
  ginecologia: "Ginecologo",
  ginecologia_ostetricia: "Ginecologo",
  ostetricia: "Ostetrico",
  ortopedia: "Ortopedico",
  pediatria: "Pediatra",
  neurologia: "Neurologo",
  neurochirurgia: "Neurochirurgo",
  oncologia: "Oncologo",
  radiologia: "Radiologo",
  radioterapia: "Radioterapista",
  anestesia: "Anestesista",
  anestesiologia: "Anestesista",
  chirurgia: "Chirurgo",
  chirurgia_generale: "Chirurgo",
  chirurgia_vascolare: "Chirurgo vascolare",
  chirurgia_plastica: "Chirurgo plastico",
  ematologia: "Ematologo",
  endocrinologia: "Endocrinologo",
  gastroenterologia: "Gastroenterologo",
  nefrologia: "Nefrologo",
  oculistica: "Oculista",
  oftalmologia: "Oculista",
  otorinolaringoiatria: "Otorinolaringoiatra",
  otorino: "Otorinolaringoiatra",
  pneumologia: "Pneumologo",
  psichiatria: "Psichiatra",
  reumatologia: "Reumatologo",
  urologia: "Urologo",
  medicina_interna: "Internista",
  geriatria: "Geriatra",
  odontoiatria: "Odontoiatra",
  odontostomatologia: "Odontoiatra",
  allergologia: "Allergologo",
  fisiatria: "Fisiatra",
  medicina_fisica_e_riabilitativa: "Fisiatra",
  igiene: "Igienista",
  medicina_del_lavoro: "Medico del lavoro",
  medicina_dello_sport: "Medico dello sport",
  medicina_legale: "Medico legale",
  patologia_clinica: "Patologo clinico",
  microbiologia: "Microbiologo",
  anatomia_patologica: "Anatomopatologo",
  infettivologia: "Infettivologo",
  malattie_infettive: "Infettivologo",
  diabetologia: "Diabetologo",
  angiologia: "Angiologo",
};

function _slug(s) {
  return (s || "").trim().toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

export function specialistNoun(spec) {
  if (!spec) return null;
  const raw = spec.trim();
  const slug = _slug(raw);
  if (SPECIALTY_NOUNS[slug]) return SPECIALTY_NOUNS[slug];
  // Prova con la prima parte prima di virgola/slash/·
  const first = raw.split(/[,;/·]/)[0].trim();
  const firstSlug = _slug(first);
  if (SPECIALTY_NOUNS[firstSlug]) return SPECIALTY_NOUNS[firstSlug];
  // Fallback: capitalizza la specializzazione originale (es. "Cardiologia" → "Cardiologia")
  return first.charAt(0).toUpperCase() + first.slice(1);
}

export function userDisplayName(u) {
  if (!u) return "";
  const isOrg = u.profession === "laboratorio" || u.profession === "diagnostica";
  if (isOrg && (u.company_name || "").trim()) return u.company_name.trim();
  const full = `${u.first_name || ""} ${u.last_name || ""}`.trim();
  return full || u.name || u.email || "";
}

// Prima lettera maiuscola su ogni parola (gestisce apostrofi, trattini e "de'/dell'/di ")
function titleCase(str) {
  return (str || "")
    .toLocaleLowerCase("it-IT")
    .split(/(\s|-|')/)
    .map((tok) => {
      if (!tok || /^[\s\-']$/.test(tok)) return tok;
      return tok.charAt(0).toLocaleUpperCase("it-IT") + tok.slice(1);
    })
    .join("");
}

export function userGreeting(u) {
  if (!u) return "Benvenuto";
  const isOrg = u.profession === "laboratorio" || u.profession === "diagnostica";
  // L'admin del sistema (Owner medico) va salutato come "dott." anche se il ruolo tecnico è laboratorio/admin
  if (isOrg && !u.is_admin && (u.company_name || "").trim()) return `Benvenuto ${u.company_name.trim()}`;
  const nome = titleCase((u.first_name || "").trim());
  const cognome = titleCase((u.last_name || "").trim());
  const fullName = `${nome} ${cognome}`.trim();
  const isMedico = ["medico_specialista", "medico_base", "medico_medicina_generale", "biologo_nutrizionista"].includes(u.profession || u.role);
  const useDott = isMedico || !!u.is_admin;
  if (fullName) {
    return useDott ? `Benvenuto dott. ${fullName}` : `Benvenuto ${fullName}`;
  }
  return "Benvenuto";
}

/**
 * Restituisce solo la parte "nome" del saluto (senza la parola "Benvenuto"),
 * utile quando si vuole mostrare "Benvenuto" su una riga e il nome sotto.
 */
export function userGender(u) {
  const g = (u?.gender || "").toUpperCase();
  if (g.startsWith("F")) return "F";
  if (g.startsWith("M")) return "M";
  // Fallback: giorno di nascita nel codice fiscale (+40 per le donne)
  const cf = (u?.fiscal_code || "").toUpperCase();
  if (cf.length === 16) { const day = parseInt(cf.slice(9, 11), 10); if (day > 40) return "F"; if (day > 0) return "M"; }
  return "";
}

export function userGreetingName(u) {
  if (!u) return "";
  const isOrg = u.profession === "laboratorio" || u.profession === "diagnostica";
  if (isOrg && !u.is_admin && (u.company_name || "").trim()) return u.company_name.trim();
  const nome = titleCase((u.first_name || "").trim());
  const cognome = titleCase((u.last_name || "").trim());
  const fullName = `${nome} ${cognome}`.trim();
  if (!fullName) return "";
  const isMedico = ["medico_specialista", "medico_base", "medico_medicina_generale", "biologo_nutrizionista"].includes(u.profession || u.role);
  const useDott = isMedico || !!u.is_admin;
  const title = userGender(u) === "F" ? "dott.ssa" : "dott.";
  return useDott ? `${title} ${fullName}` : fullName;
}

export function userInitials(u) {
  const dn = userDisplayName(u) || "?";
  return dn.split(" ").slice(0, 2).map((s) => s[0] || "").join("").toUpperCase() || "?";
}

export function professionalTitle(profession, specialization) {
  if (profession === "medico_specialista") {
    return specialistNoun(specialization) || "Medico Specialista";
  }
  if (profession === "medico_base" || profession === "medico_medicina_generale") {
    return "Medico di Base";
  }
  if (profession === "laboratorio") return "Laboratorio di Analisi";
  if (profession === "diagnostica") return "Struttura Diagnostica";
  if (profession === "altro_professionista") {
    return specialization ? (specialization.charAt(0).toUpperCase() + specialization.slice(1)) : "Professionista sanitario";
  }
  return ROLE_LABELS[profession] || profession;
}

export const CATEGORY_LABELS = {
  richiesta_prescrizione: "Richiesta prescrizione",
  richiesta_consulto: "Richiesta consulto",
  invio_referto: "Condivisione documento",
  generica: "Richiesta chiarimento / integrazione",
  // legacy retro-compat (visualizzazione)
  follow_up: "Follow-up",
  richiesta_informazioni: "Richiesta informazioni",
  valore_allarme: "Valore di allarme",
  errore_richiesta: "Errore nella richiesta",
  ripetere_esame: "Ripetere esame",
  aggiungere_esame: "Aggiungere esame",
  richiesta_chiarimento: "Richiesta chiarimento",
};

export const REQUEST_TYPES = [
  { key: "richiesta_prescrizione", label: "Richiesta prescrizione", icon: "FlaskConical",
    hint: "Invia indicazioni al MMG." },
  { key: "richiesta_consulto", label: "Richiesta consulto", icon: "ClipboardList",
    hint: "Chiedi un parere specialistico." },
  { key: "invio_referto", label: "Condivisione documento", icon: "FileText",
    hint: "Invia referti o documentazione." },
  { key: "generica", label: "Richiesta chiarimento / integrazione", icon: "HelpCircle",
    hint: "Richiedi chiarimenti su un referto, un esame o una documentazione ricevuta." },
];

export const STATUS_LABELS = {
  inviata:         { label: "Inviata",         color: "slate",   dot: "bg-slate-400" },
  ricevuta:        { label: "Ricevuta",        color: "sky",     dot: "bg-sky-500" },
  presa_in_carico: { label: "Presa in carico", color: "indigo",  dot: "bg-indigo-500" },
  in_lavorazione:  { label: "In lavorazione",  color: "amber",   dot: "bg-amber-500" },
  completata:      { label: "Completata",      color: "emerald", dot: "bg-emerald-500" },
  rifiutata:       { label: "Rifiutata",       color: "red",     dot: "bg-red-500" },
  annullata:       { label: "Annullata",       color: "zinc",    dot: "bg-zinc-400" },
};

export const PRIORITY_LABELS = {
  bassa:    "Bassa",
  normale:  "Normale",
  alta:     "Alta",
  critica:  "Critica",
};

export const URGENCY_LABELS = {
  normale: "Normale",
  alto: "Alto",
  critico: "Critico",
};

// Elenco specializzazioni comuni per il dropdown (fonte: SPECIALTY_NOUNS keys)
// Etichetta = versione umanamente leggibile.
export const SPECIALIZATIONS_LIST = [
  "Allergologia","Anatomia patologica","Anestesia","Angiologia",
  "Cardiologia","Chirurgia generale","Chirurgia plastica","Chirurgia vascolare",
  "Dermatologia","Diabetologia","Ematologia","Endocrinologia",
  "Fisiatria","Gastroenterologia","Geriatria","Ginecologia",
  "Igiene","Infettivologia","Medicina del lavoro","Medicina dello sport",
  "Medicina interna","Medicina legale","Microbiologia","Nefrologia",
  "Neurochirurgia","Neurologia","Oculistica","Odontoiatria",
  "Oncologia","Ortopedia","Otorinolaringoiatria","Patologia clinica",
  "Pediatria","Pneumologia","Psichiatria","Radiologia",
  "Radioterapia","Reumatologia","Urologia",
];
